import { create } from "zustand";

import {
  emptyFilters,
  type FilterState,
  type ListMode,
  type ListTab,
} from "@/types/domain";

interface FilterStore extends FilterState {
  set: <K extends keyof FilterState>(key: K, value: FilterState[K]) => void;
  reset: () => void;
  snapshot: () => FilterState;
}

export const useFilterStore = create<FilterStore>((set, get) => ({
  ...emptyFilters,
  set: (key, value) => set({ [key]: value } as Partial<FilterState>),
  reset: () => set({ ...emptyFilters }),
  snapshot: () => {
    const { set: _s, reset: _r, snapshot: _sn, ...state } = get();
    return state as FilterState;
  },
}));

const LIST_TABS: readonly ListTab[] = [
  "all",
  "needsCategorization",
  "needsReview",
  "approved",
  "archived",
  "failed",
];

export function filterStateFromUrl(params: URLSearchParams): FilterState {
  const rawTab = params.get("tab");
  const tab = LIST_TABS.includes(rawTab as ListTab)
    ? (rawTab as ListTab)
    : emptyFilters.listTab;
  const mode: ListMode = params.get("mode") === "documents" ? "documents" : "emails";
  return {
    subject: params.get("subject") ?? "",
    from: params.get("from") ?? "",
    documentType: params.get("documentType"),
    listTab: tab,
    dateFrom: params.get("dateFrom"),
    dateTo: params.get("dateTo"),
    sort: params.get("sort") === "oldest" ? "oldest" : "newest",
    tradeKey: params.get("tradeKey") ?? "",
    correctedOnly: params.get("correctedOnly") === "true",
    mode,
  };
}

export function filterStateToUrl(state: FilterState): URLSearchParams {
  const params = new URLSearchParams();
  if (state.subject) params.set("subject", state.subject);
  if (state.from) params.set("from", state.from);
  if (state.documentType) params.set("documentType", state.documentType);
  if (state.listTab !== emptyFilters.listTab) params.set("tab", state.listTab);
  if (state.dateFrom) params.set("dateFrom", state.dateFrom);
  if (state.dateTo) params.set("dateTo", state.dateTo);
  if (state.sort !== "newest") params.set("sort", state.sort);
  if (state.tradeKey) params.set("tradeKey", state.tradeKey);
  if (state.correctedOnly) params.set("correctedOnly", "true");
  if (state.mode !== "emails") params.set("mode", state.mode);
  return params;
}
